import { useMemo } from 'react'
import { useAuthContext } from '@/features/auth/hooks/use-auth-context'
import { evaluatePermission } from '@/lib/rbac-utils'
import type { DynamicNavItem, NavItem, NavItemGroup } from '@/lib/routing/routing.types'

/**
 * Hook to filter navigation items based on RBAC permissions.
 *
 * Evaluates navigation items against user permissions using the same logic
 * as the routing system's PermissionGate component.
 *
 * @param items - Array of navigation items or groups to filter
 * @returns Filtered array of navigation items based on user permissions
 */
export function useRbacNavigation<T extends NavItem | NavItemGroup>(items: T[]): T[] {
  const { user, membership, isLoading } = useAuthContext()

  return useMemo(() => {
    if (isLoading) return []

    const userRole = membership?.role ?? undefined

    return items.filter((item) => {
      return evaluatePermission(item.meta, user, userRole)
    })
  }, [items, user, membership, isLoading])
}

/**
 * Hook to filter navigation groups and their nested items based on RBAC permissions.
 *
 * Filters both groups and their nested items, removing groups that have no visible items.
 *
 * @param groups - Array of navigation groups to filter
 * @returns Filtered array of navigation groups with filtered nested items
 */
export function useRbacNavigationGroups(groups: NavItemGroup[]): NavItemGroup[] {
  const { user, membership, isLoading } = useAuthContext()

  return useMemo(() => {
    if (isLoading) return []

    const userRole = membership?.role ?? undefined

    return groups
      .map((group) => {
        if (!('navItems' in group)) {
          const isGroupVisible = evaluatePermission(group.meta, user, userRole)
          return isGroupVisible ? group : null
        }

        // Filter group's nav items
        const filteredNavItems = group.navItems.filter((item: NavItem | DynamicNavItem) => {
          return evaluatePermission(item.meta, user, userRole)
        })

        // Return group only if it has visible items and group itself is visible
        const hasVisibleItems = filteredNavItems.length > 0
        const isGroupVisible = evaluatePermission(group.meta, user, userRole)

        if (hasVisibleItems && isGroupVisible) {
          return {
            ...group,
            navItems: filteredNavItems,
          }
        }

        return null
      })
      .filter((group): group is NavItemGroup => group !== null)
  }, [groups, user, membership, isLoading])
}
